/**
 * Scroll background + text crossfade.
 *
 * Any element on the page can opt in by adding a `data-scroll-bg-color`
 * attribute (a hex color, e.g. "#f5eefb") to its outer wrapper (typically
 * a <section>). Optionally add `data-scroll-text-color` (another hex
 * color) to the same element to also crossfade text color alongside the
 * background.
 *
 * This script tracks the vertical center of the viewport, finds the two
 * nearest "stops" above and below it, and smoothly blends:
 *   - a fixed full-viewport backdrop layer's background-color, and
 *   - a --scroll-text-color CSS custom property on <html>,
 * between each stop's colors as the user scrolls — the same effect used
 * on richardmille.com.
 *
 * Usage: include this file once, sitewide (e.g. via the snippet
 * 'scroll-bg-crossfade.liquid'), add data-scroll-bg-color="#hex" (and
 * optionally data-scroll-text-color="#hex") to any section that should
 * act as a color stop, and use `color: var(--scroll-text-color);` on
 * whatever text inside that section should follow the fade.
 */
(function () {
  "use strict";

  var backdrop = null;
  var root = null;
  var stops = []; // { top: number, color: [r,g,b], textColor: [r,g,b] | null }
  var ticking = false;

  function hexToRgb(hex) {
    hex = hex.replace("#", "");
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map(function (c) {
          return c + c;
        })
        .join("");
    }
    var num = parseInt(hex, 16);
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function lerpColor(a, b, t) {
    return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
  }

  function rgbString(c) {
    return "rgb(" + Math.round(c[0]) + "," + Math.round(c[1]) + "," + Math.round(c[2]) + ")";
  }

  function collectStops() {
    var nodes = document.querySelectorAll("[data-scroll-bg-color]");
    var scrollY = window.pageYOffset || document.documentElement.scrollTop;

    stops = Array.prototype.map
      .call(nodes, function (el) {
        var rect = el.getBoundingClientRect();
        var textAttr = el.getAttribute("data-scroll-text-color");
        return {
          top: rect.top + scrollY,
          color: hexToRgb(el.getAttribute("data-scroll-bg-color")),
          textColor: textAttr ? hexToRgb(textAttr) : null,
        };
      })
      .sort(function (a, b) {
        return a.top - b.top;
      });
  }

  // Returns { bg: [r,g,b], text: [r,g,b] | null } for the current scroll position.
  function currentColors() {
    if (!stops.length) return null;

    var scrollY = window.pageYOffset || document.documentElement.scrollTop;
    var ref = scrollY + window.innerHeight / 2;

    function pick(stop) {
      return { bg: stop.color, text: stop.textColor };
    }

    if (ref <= stops[0].top) return pick(stops[0]);
    if (ref >= stops[stops.length - 1].top) return pick(stops[stops.length - 1]);

    for (var i = 0; i < stops.length - 1; i++) {
      var a = stops[i];
      var b = stops[i + 1];
      if (ref >= a.top && ref <= b.top) {
        var t = b.top === a.top ? 0 : (ref - a.top) / (b.top - a.top);
        var bg = lerpColor(a.color, b.color, t);
        // Only blend text color when BOTH stops either side define one —
        // otherwise leave it alone so a section without a text color
        // doesn't drag everything toward black/white.
        var text = a.textColor && b.textColor ? lerpColor(a.textColor, b.textColor, t) : null;
        return { bg: bg, text: text };
      }
    }
    return pick(stops[0]);
  }

  function applyColors() {
    ticking = false;
    if (!backdrop) return;
    var c = currentColors();
    if (!c) return;

    backdrop.style.backgroundColor = rgbString(c.bg);

    if (c.text && root) {
      root.style.setProperty("--scroll-text-color", rgbString(c.text));
    }
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(applyColors);
      ticking = true;
    }
  }

  function init() {
    backdrop = document.getElementById("scroll-bg-backdrop");
    root = document.documentElement;
    if (!backdrop) return;

    collectStops();
    applyColors();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", function () {
      collectStops();
      applyColors();
    });

    // Re-scan if sections load/resize later (e.g. images finish loading,
    // or the theme editor swaps section content).
    window.addEventListener("load", function () {
      collectStops();
      applyColors();
    });
    document.addEventListener("shopify:section:load", function () {
      collectStops();
      applyColors();
    });
    document.addEventListener("shopify:section:unload", function () {
      collectStops();
      applyColors();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();